// Bulk owner changes (ports of AppStore.assign(recordIds:to:) and the iOS "Bulk assign" impact numbers in
// TerritoryDetailView): the record edits that give many stores one owner, as `Patch` lists for `useSave()`, and the
// owner picker both owner dialogs use (map Assign, Stores › Import).
//
// Unassigned is a nil `ownerId` (Record.swift `ownerId: UUID?`), which the wire writes as an omitted key, so the patch
// removes the key rather than storing "" or null. Stores that already have the chosen owner get no patch, so a
// re-run of the same assignment queues nothing.
//
// iOS `assign` goes through `updateRecord`, which logs an "Assigned" activity (from → to) for every owner change; with
// an `AssignLog` the console writes the same entry next to each store's edit, so History › Changes and the
// Assignments report show web assigns too. A store deleted on another device between the dialog opening and the save
// gets no edit (the replica skips it) but still gets its entry, which then points at nothing, like any activity of a
// deleted store.

import { USER_ROLE_TITLES } from '@/contracts/enums';
import { newActivity, type RecordPayload, type UserPayload } from '@/contracts/models';
import type { JsonObject } from '@/contracts/wire';
import { toWireDate } from '@/contracts/wireDate';
import { patch, type Patch } from '@/sync/patch';
import { compareLocalized, fullName, idKey, sameId, userName } from './util';

/** Most stores handed to one `save` call by a bulk assign (each call persists the replica once). */
export const ASSIGN_CHUNK_SIZE = 500;

/** The owner pickers' value for "Unassigned" (never a UUID, so it cannot clash with a person). */
export const UNASSIGNED_OWNER_VALUE = 'none';

/**
 * The owner picker of the map's Assign and of Stores › Import: "Unassigned" first when it is offered, then the people
 * in the given order (`pickerUsers`: by name) as "Name · Role", the viewer marked "(you)".
 */
export function ownerPickerOptions(
  people: readonly Pick<UserPayload, 'id' | 'firstName' | 'lastName' | 'role'>[],
  meId: string | null | undefined,
  options: { unassigned: boolean },
): { value: string; label: string }[] {
  const out = options.unassigned ? [{ value: UNASSIGNED_OWNER_VALUE, label: 'Unassigned' }] : [];
  for (const u of people) {
    const name = `${fullName(u) || 'Unnamed'}${sameId(u.id, meId) ? ' (you)' : ''}`;
    out.push({ value: u.id, label: `${name} · ${USER_ROLE_TITLES[u.role]}` });
  }
  return out;
}

/** True when the store already has this owner (`null` = Unassigned; ids compared in any case). */
export function hasOwner(record: Pick<RecordPayload, 'ownerId'>, ownerId: string | null): boolean {
  return ownerId ? sameId(record.ownerId, ownerId) : !record.ownerId;
}

/** The stores whose owner would change (iOS "Owner will change"), each id once, in the given order. */
export function ownerChanges<R extends Pick<RecordPayload, 'id' | 'ownerId'>>(records: readonly R[], ownerId: string | null): R[] {
  const seen = new Set<string>();
  const out: R[] = [];
  for (const r of records) {
    const key = idKey(r.id);
    if (seen.has(key)) continue;
    seen.add(key);
    if (!hasOwner(r, ownerId)) out.push(r);
  }
  return out;
}

/** Who assigns and when, for the "Assigned" activity of each changed store (see the file comment). */
export interface AssignLog {
  /** The viewer's profile (the activity's `userId`). */
  by: string | null | undefined;
  at: Date;
  /** People by lower-case id, to name the old and new owner (`userName`: "Unassigned" when not found). */
  users: ReadonlyMap<string, { firstName: string; lastName: string }>;
}

/**
 * `assign(recordIds:to:)`, one group per store whose owner changes (stores that already have this owner are
 * skipped): the `patch.set('records', id, {ownerId})` and, with `log`, its "Assigned" activity right after it. Save a
 * group in one `save` call so a store never gets one without the other. `null` assigns nobody (the key is removed).
 */
export function assignOwnerEdits(
  records: readonly Pick<RecordPayload, 'id' | 'ownerId'>[],
  ownerId: string | null,
  log?: AssignLog,
): Patch[][] {
  const at = log ? toWireDate(log.at) : '';
  const toName = log ? userName(log.users, ownerId) : '';
  return ownerChanges(records, ownerId).map((r) => {
    const edit = patch.set('records', r.id, { ownerId: ownerId ?? undefined });
    if (!log) return [edit];
    // AppStore.updateRecord: Activity(recordId:, type: .assignment, title: "Assigned", userId:, completedAt: .now,
    // fromValue: userName(old.ownerId), toValue: userName(new.ownerId)).
    const entry = newActivity({
      recordId: r.id,
      type: 'assignment',
      title: 'Assigned',
      ...(log.by ? { userId: log.by } : {}),
      createdAt: at,
      completedAt: at,
      fromValue: userName(log.users, r.ownerId),
      toValue: toName,
    });
    return [edit, patch.create('activities', entry.id, entry as unknown as JsonObject)];
  });
}

/** `assignOwnerEdits` as one flat list (one record edit per changed store, each followed by its activity with `log`). */
export function assignOwnerPatches(records: readonly Pick<RecordPayload, 'id' | 'ownerId'>[], ownerId: string | null, log?: AssignLog): Patch[] {
  return assignOwnerEdits(records, ownerId, log).flat();
}

/**
 * The stores as they are once `ids` (lower-case) have this owner (null: the key removed, as the edit does): after a
 * bulk assign stopped part way, so "Try again" only changes, and logs, the stores that did not change yet.
 */
export function withOwnerOn<R extends Pick<RecordPayload, 'id' | 'ownerId'>>(records: readonly R[], ids: ReadonlySet<string>, ownerId: string | null): R[] {
  return records.map((r) => {
    if (!ids.has(idKey(r.id))) return r;
    if (ownerId) return { ...r, ownerId };
    const { ownerId: _removed, ...rest } = r;
    return rest as R;
  });
}

/** Splits a list into runs of at most `size` items (the last may be shorter). A size below 1 counts as 1. */
export function chunked<T>(items: readonly T[], size: number = ASSIGN_CHUNK_SIZE): T[][] {
  const step = Math.max(1, Math.floor(size));
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += step) out.push(items.slice(i, i + step));
  return out;
}

export interface OwnerShare {
  /** The owner's id as stored on the first store seen; null for Unassigned and for "Unknown person". */
  ownerId: string | null;
  /** Full name ("Unnamed" without one), "Unassigned", or "Unknown person" for owners the viewer cannot see. */
  name: string;
  count: number;
}

/**
 * Who owns the stores now, most stores first, then by name (the iOS impact list of current owners). Owners missing
 * from `users` (not visible to the viewer, or deleted) are counted together as "Unknown person", never as
 * "Unassigned", which would read as two Unassigned rows.
 */
export function ownerBreakdown(
  records: readonly Pick<RecordPayload, 'ownerId'>[],
  users: ReadonlyMap<string, { firstName: string; lastName: string }>,
): OwnerShare[] {
  const byOwner = new Map<string, OwnerShare>();
  for (const r of records) {
    const user = r.ownerId ? users.get(idKey(r.ownerId)) : undefined;
    const key = !r.ownerId ? '' : user ? idKey(r.ownerId) : '?';
    const share = byOwner.get(key);
    if (share) share.count += 1;
    else if (key === '') byOwner.set(key, { ownerId: null, name: 'Unassigned', count: 1 });
    else if (key === '?') byOwner.set(key, { ownerId: null, name: 'Unknown person', count: 1 });
    else byOwner.set(key, { ownerId: r.ownerId ?? null, name: fullName(user) || 'Unnamed', count: 1 });
  }
  return [...byOwner.values()].sort((a, b) => b.count - a.count || compareLocalized(a.name, b.name));
}
